import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { CalendarDays, Trophy, Users, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

export default function Landing() {
    const { t } = useLanguage();
    const [form, setForm] = useState({ name: "", mobile: "", message: "" });
    const [sending, setSending] = useState(false);

    const submitContact = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success(t("landing.contactSuccess"));
            setForm({ name: "", mobile: "", message: "" });
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="glass sticky top-0 z-20 border-b border-border">
                <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        <img src="https://customer-assets.emergentagent.com/job_schedule-sync-109/artifacts/0i7pk9q1_refpairlogo_trans.png" alt="RefPair" className="w-12 h-12 object-contain" />
                        <span className="font-heading text-lg font-semibold tracking-tight">RefPair<span className="text-muted-foreground font-normal text-[0.72em] tracking-tighter -ml-[1px]">.com</span></span>
                    </div>
                    <nav className="flex items-center gap-3">
                        <LanguageSwitcher />
                    </nav>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-8 pt-8 pb-24 text-center space-y-10">
                <div className="flex justify-center">
                    <Link to="/login">
                        <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-10 h-12" data-testid="landing-login-cta">
                            {t("common.signIn")}
                        </Button>
                    </Link>
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95]">
                    {t("landing.heroLine1")}<br/>
                    <span className="text-primary">{t("landing.heroLine2")}</span>
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    {t("landing.heroSubtitle")}
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/register">
                        <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 h-12" data-testid="landing-primary-cta">
                            {t("common.createAccount")} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                    <Link to="/login">
                        <Button size="lg" variant="outline" className="rounded-md px-8 h-12" data-testid="landing-secondary-cta">
                            {t("landing.iAlreadyHaveAccount")}
                        </Button>
                    </Link>
                </div>

                <div className="space-y-1.5 text-sm sm:text-base text-foreground" data-testid="landing-taglines">
                    <p data-testid="landing-tagline-1">{t("landing.tagline1")}</p>
                    <p data-testid="landing-tagline-2">{t("landing.tagline2")}</p>
                    <p data-testid="landing-tagline-3">{t("landing.tagline3")}</p>
                </div>

                <div className="space-y-6">
                    <div
                        id="contact-form-inline"
                        className="max-w-md mx-auto text-left bg-white border border-border rounded-md p-6 sm:p-8 shadow-sm"
                        data-testid="contact-dialog"
                    >
                        <h2 className="font-heading text-xl font-semibold mb-4">{t("landing.contactDialogTitle")}</h2>
                        <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
                            <div className="space-y-2">
                                <Label htmlFor="contact-name">{t("common.name")}</Label>
                                <Input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="h-11 rounded-md" data-testid="contact-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact-mobile">{t("common.mobileNumber")}</Label>
                                <Input id="contact-mobile" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                                    className="h-11 rounded-md" data-testid="contact-mobile-input" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact-message">{t("common.message")}</Label>
                                <Textarea id="contact-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    rows={5} maxLength={5000}
                                    placeholder={t("landing.contactMessagePlaceholder")}
                                    className="rounded-md min-h-[120px]" data-testid="contact-message-input" />
                            </div>
                            <div className="flex justify-end pt-2">
                                <Button type="submit" disabled={sending} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="contact-send-btn">
                                    {sending ? t("landing.sending") : t("landing.sendMessage")}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="pt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-border text-left">
                    <div className="py-8 md:pr-8 md:border-r border-border">
                        <CalendarDays className="w-5 h-5 text-primary mb-3" />
                        <div className="font-heading font-medium">{t("landing.feature1Title")}</div>
                        <div className="text-sm text-muted-foreground">{t("landing.feature1Sub")}</div>
                    </div>
                    <div className="py-8 md:px-8 md:border-r border-border">
                        <Users className="w-5 h-5 text-primary mb-3" />
                        <div className="font-heading font-medium">{t("landing.feature2Title")}</div>
                        <div className="text-sm text-muted-foreground">{t("landing.feature2Sub")}</div>
                    </div>
                    <div className="py-8 md:pl-8">
                        <Trophy className="w-5 h-5 text-primary mb-3" />
                        <div className="font-heading font-medium">{t("landing.feature3Title")}</div>
                        <div className="text-sm text-muted-foreground">{t("landing.feature3Sub")}</div>
                    </div>
                </div>
            </main>
        </div>
    );
}
